// Minimal, dependency-free Markdown -> safe HTML renderer.
// Scope is intentionally small (what LLM chat replies use): headings, bold,
// italic, inline code, fenced/indented code, links, ordered/unordered lists,
// blockquotes, horizontal rules, tables, paragraphs. All raw text is HTML-escaped
// first, so no untrusted markup can pass through.

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}

// Inline: code spans, bold, italic, strikethrough, links. Operates on already-escaped text.
function inline(text: string): string {
  const codes: string[] = [];
  let t = text.replace(/`([^`]+)`/g, (_m, code) => {
    codes.push(`<code>${code}</code>`);
    return `\u0000${codes.length - 1}\u0000`;
  });

  t = t
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>")
    .replace(/(^|[^_])_([^_\n]+)_/g, "$1<em>$2</em>")
    .replace(/~~([^~]+)~~/g, "<del>$1</del>");

  t = t.replace(/\u0000(\d+)\u0000/g, (_m, i) => codes[Number(i)]);
  return t;
}

export function renderMarkdown(md: string): string {
  const src = escapeHtml(md.replace(/\r\n?/g, "\n"));
  const lines = src.split("\n");
  const out: string[] = [];
  let i = 0;

  const flushParagraph = (buf: string[]) => {
    if (!buf.length) return;
    out.push(`<p>${inline(buf.join(" ")).trim()}</p>`);
    buf.length = 0;
  };

  let para: string[] = [];

  while (i < lines.length) {
    const line = lines[i];

    // fenced code block
    const fence = line.match(/^\s*```(.*)$/);
    if (fence) {
      flushParagraph(para);
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) body.push(lines[i++]);
      i++; // closing fence
      out.push(`<pre><code>${body.join("\n")}</code></pre>`);
      continue;
    }

    // heading
    const head = line.match(/^(#{1,6})\s+(.*)$/);
    if (head) {
      flushParagraph(para);
      const lvl = Math.min(head[1].length + 2, 6); // shift down so # -> h3 in a narrow pane
      out.push(`<h${lvl}>${inline(head[2].trim())}</h${lvl}>`);
      i++;
      continue;
    }

    // horizontal rule
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      flushParagraph(para);
      out.push("<hr />");
      i++;
      continue;
    }

    // blockquote (collapse consecutive). Note: text is already HTML-escaped,
    // so the ">" marker appears as "&gt;".
    if (/^\s*&gt;\s?/.test(line)) {
      flushParagraph(para);
      const quote: string[] = [];
      while (i < lines.length && /^\s*&gt;\s?/.test(lines[i])) quote.push(lines[i++].replace(/^\s*&gt;\s?/, ""));
      out.push(`<blockquote>${inline(quote.join(" "))}</blockquote>`);
      continue;
    }

    // table: header row + separator row of ---|---
    if (/\|/.test(line) && i + 1 < lines.length && /^\s*\|?\s*:?-{1,}.*\|/.test(lines[i + 1])) {
      flushParagraph(para);
      const splitRow = (r: string) =>
        r.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => inline(c.trim()));
      const header = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /\|/.test(lines[i]) && lines[i].trim()) rows.push(splitRow(lines[i++]));
      const cellStyle = "border:1px solid #888;padding:4px 8px;text-align:left";
      const thead = `<thead><tr>${header.map((c) => `<th style="${cellStyle}">${c}</th>`).join("")}</tr></thead>`;
      const tbody = `<tbody>${rows
        .map((r) => `<tr>${r.map((c) => `<td style="${cellStyle}">${c}</td>`).join("")}</tr>`)
        .join("")}</tbody>`;
      out.push(`<table style="border-collapse:collapse;width:100%">${thead}${tbody}</table>`);
      continue;
    }

    // lists (unordered or ordered), allowing nested via indentation
    if (/^\s*([-*+]|\d+[.)])\s+/.test(line)) {
      flushParagraph(para);
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*+]|\d+[.)])\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*+]|\d+[.)])\s+/, ""));
        i++;
      }
      const tag = ordered ? "ol" : "ul";
      out.push(`<${tag}>${items.map((it) => `<li>${inline(it.trim())}</li>`).join("")}</${tag}>`);
      continue;
    }

    // blank line ends a paragraph
    if (!line.trim()) {
      flushParagraph(para);
      i++;
      continue;
    }

    para.push(line.trim());
    i++;
  }
  flushParagraph(para);
  return out.join("\n");
}
